import { useState, useEffect, useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Citation, Message as MessageType } from "@/lib/api/types";
import { useStore } from "@/state/useState";
import Markdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkCitations from "@/lib/markdown/remarkCitations";
import normalizeLinkDestinations from "@/lib/markdown/normalizeLinkDestinations";
import {
  ThumbsUp,
  ThumbsDown,
  Clipboard,
  ClipboardCheck,
  ClipboardX,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { parseISO, format, formatDistanceToNow } from "date-fns";
import { fetchWithAuth, urlBuilder } from "@/lib/api/utils";
import { copyText } from "@/lib/clipboard";
import answerForCopy from "@/lib/markdown/answerForCopy";

function MessageContent({
  text,
  // image,
  citations,
  created_at,
  getCitation,
}: MessageType["content"] & {
  created_at: string;
  getCitation: (citation: Citation) => void;
}) {
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (text === "") {
      const interval = setInterval(() => {
        setDots((prevDots) => (prevDots.length >= 3 ? "" : prevDots + "."));
      }, 500);

      return () => clearInterval(interval);
    }
  }, [text]);

  const formatMessageDate = (dateString: string) => {
    const date = parseISO(dateString);
    const formattedDate = format(date, "MMM d 'at' h:mm a");
    const timeAgo = formatDistanceToNow(date, { addSuffix: true });
    return `${formattedDate} (${timeAgo})`;
  };
  // A [docN] marker is an index into the model's retrieved-chunk list, which is
  // not what we receive: the server sends only the cited documents, each listing
  // the markers that point at it. So resolve by marker, never by array position.
  const byMarker = useMemo(() => {
    const map = new Map<number, Citation>();
    for (const citation of citations ?? []) {
      for (const marker of citation.markers ?? []) map.set(marker, citation);
    }
    return map;
  }, [citations]);

  const components: Partial<Components> = {
    // Tailwind's preflight zeroes the margin on every block element, and nothing
    // here restores it: a message written as several paragraphs rendered as one
    // wall of text. Most visible on the change-request confirmation, where the
    // assembled form sits between an intro line and the "Klopt dit?" question and
    // the user is being asked to check it before an irreversible write.
    //
    // `last:mb-0` so a single-paragraph message -- almost every message -- keeps
    // its current spacing exactly.
    p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
    a: (props) => {
      const { href, children } = props;
      const match = href?.match(/^citation-(\d+)$/);
      if (!match)
        return (
          <a
            href={href}
            className="text-blue-500 hover:underline"
            target="_blank"
          >
            {children}
          </a>
        );
      const citation = byMarker.get(parseInt(match[1]));
      // Unresolvable: the model invented a marker, or this is a message stored
      // before the server started sending `markers`. Render nothing rather than
      // leaking "[doc7]" to the user or labelling a source that does not exist.
      if (!citation) return null;
      return (
        <Button
          variant="link"
          className="p-0 h-auto"
          onClick={() => getCitation(citation)}
        >
          [{citation.display_index}]
        </Button>
      );
    },
  };

  return (
    <div>
      <Markdown
        remarkPlugins={[remarkGfm, remarkCitations]}
        components={components}
      >
        {/* Dutch, like the rest of the user-facing copy (MA-204). This is the
            single most-seen string in the app -- every answer starts here -- and it
            was the last English one left. */}
        {text !== ""
          ? // Knowledge-base URLs arrive with the line wrapping of the document they
            // were pasted from, which puts spaces inside them and stops markdown from
            // forming a link at all (MA-222).
            normalizeLinkDestinations(text)
          : `Antwoord wordt gegenereerd${dots}`}
      </Markdown>

      <span className="text-xs text-muted-foreground mt-1 block">
        {formatMessageDate(created_at)}
      </span>
    </div>
  );
}

export default function Message({
  content,
  created_at,
  id,
  role,
  feedback,
}: MessageType) {
  const documents = useStore((state) => state.documents);
  const loadDocuments = useStore((state) => state.loadDocuments);
  const loadDocument = useStore((state) => state.loadDocument);

  const handleDownload = async (documentId: string) => {
    const doc = await loadDocument(documentId);
    if (doc.type === "topdesk_knowledge_item") {
    
      const urlParams = new URLSearchParams(new URL(doc.origin, "https://mboamersfoort.topdesk.net").search);
      const unid = urlParams.get('unid');
    
    
      const knowledgeUrl = `https://mboamersfoort.topdesk.net/tas/public/ssp/content/detail/knowledgeitem?unid=${unid}&origin=searchPreview`;
      window.open(knowledgeUrl, "_blank");
      return;
    } else if (doc.type === "url") {
      window.open(doc.origin, "_blank");
      return;
    }

    await loadDocuments();
    try {
      const response = await fetchWithAuth(
        urlBuilder(`/doc/download/${documentId}`)
      );
      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Get the document from our current documents state
      const documentData = documents.find((doc) => doc.id === documentId);
      const filename = documentData
        ? documentData.origin
        : `document-${documentId}`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading document:", error);
      alert("Downloaden van document is mislukt.");
    }
  };
  const getCitation = (citation: Citation) => {
    // getDocument
    return handleDownload(citation.document_id);
  };

  const isUser = role === "user";
  const [citationsOpen, setCitationsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackOptions, setFeedbackOptions] = useState<string[]>([]);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const submitFeedback = useStore((state) => state.submitFeedback);

  const feedbackTypes = {
    missing_citation: "Bronvermeldingen ontbreken",
    wrong_citation: "Bronvermeldingen zijn onjuist",
    out_of_scope: "Het antwoord komt niet uit mijn data",
    other_unhelpful: "Anders (niet nuttig)",
    inaccurate_or_irrelevant: "Onjuiste of irrelevante informatie",
  };

  useEffect(() => {
    if (feedback) {
      const feedbackArray = feedback.split(", ");
      setFeedbackOptions(feedbackArray);
    }
  }, [feedback, content.text]);

  // The tooltip alone is not feedback: Radix only mounts its content while the
  // trigger is hovered or focused, so a failure shown only there is nearly as
  // invisible as the `console.error` this replaced. The icon and the accessible name
  // carry it too (MA-228).
  const copyLabels = {
    idle: "Kopiëren naar klembord",
    copied: "Gekopieerd!",
    failed: "Kopiëren lukt hier niet",
  } as const;
  const CopyIcon = {
    idle: Clipboard,
    copied: ClipboardCheck,
    failed: ClipboardX,
  }[copyState];

  const handleCopy = async () => {
    // Reports failure rather than logging it: in the portal iframe the Clipboard API
    // is blocked by the parent's permission policy, and this button used to be a
    // silent no-op there (MA-228).
    //
    // Copies the rendered form, not content.text: the stored answer carries the
    // raw [docN] markers that only the render resolves, so copying it verbatim
    // pasted "[doc1]" into tickets and documents (MA-237).
    const copied = await copyText(
      answerForCopy(content.text, content.citations ?? []),
    );
    setCopyState(copied ? "copied" : "failed");
    setTimeout(() => setCopyState("idle"), 2000);
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      <div
        className={`flex ${
          isUser ? "flex-row-reverse" : "flex-row"
        } items-start w-3/4`}
      >
        {/* The assistant shows the Sam mark -- the same asset as the browser
            favicon and the Teams app icon, so the app looks like one product
            wherever it is embedded. The user has no image: we have no photo to
            show, and pointing at a file that does not exist is what this
            replaces. Both avatars previously requested /ai-avatar.png and
            /user-avatar.png, neither of which was ever in public/, so every
            rendered message 404'd twice and fell back to these letters by
            accident rather than by design. The letter is now the intended
            treatment for the user (MA-199). */}
        <Avatar className="w-8 h-8 mr-2">
          {isUser ? (
            <AvatarFallback>U</AvatarFallback>
          ) : (
            <>
              <AvatarImage src="/sam-icon.svg" alt="Sam" />
              <AvatarFallback>AI</AvatarFallback>
            </>
          )}
        </Avatar>
        <Card
          className={`${
            isUser ? "bg-primary text-primary-foreground w-full" : "w-full"
          }`}
        >
          <CardContent className="p-3">
            <MessageContent {...{ ...content, created_at, getCitation }} />
          </CardContent>
          {!isUser && (
            <CardFooter
              className={`flex flex-col items-start space-y-2 p-3 pt-0 ${
                content.citations ? "pb-2" : ""
              }`}
            >
              <div className="flex space-x-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={
                          feedback === "positive" ? "secondary" : "ghost"
                        }
                        size="icon-small"
                        onClick={() => submitFeedback(id, "positive")}
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Markeer als nuttig</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DialogTrigger asChild>
                          <Button
                            variant={
                              feedback && feedback !== "positive"
                                ? "secondary"
                                : "ghost"
                            }
                            size="icon-small"
                          >
                            <ThumbsDown className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Markeer als niet nuttig</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Feedback</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Je feedback helpt ons om Sam te verbeteren.
                      </p>
                      <p>Wat was er niet goed aan dit antwoord?</p>
                      {Object.entries(feedbackTypes).map(([key, value]) => (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={key}
                            checked={feedbackOptions.includes(key)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFeedbackOptions([...feedbackOptions, key]);
                              } else {
                                setFeedbackOptions(
                                  feedbackOptions.filter((item) => item !== key)
                                );
                              }
                            }}
                          />
                          <Label htmlFor={key}>{value}</Label>
                        </div>
                      ))}
                      <Button
                        onClick={() => {
                          submitFeedback(id, feedbackOptions.join(", "));
                          setFeedbackOpen(false);
                        }}
                      >
                        Feedback versturen
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-small"
                        onClick={handleCopy}
                        aria-label={copyLabels[copyState]}
                      >
                        <CopyIcon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{copyLabels[copyState]}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {content.citations && content.citations.length > 0 && (
                <Collapsible
                  open={citationsOpen}
                  onOpenChange={setCitationsOpen}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex justify-between"
                    >
                      <span>Sources: {content.citations.length}</span>
                      {citationsOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2 mb-4 flex flex-col w-1/2">
                    {content.citations.map((citation, index) => (
                      <Button
                        key={index}
                        variant="link"
                        size="sm"
                        className="h-auto text-xs justify-start"
                        // onClick={() => {openWindow}}
                        onClick={() => {
                          getCitation(citation);
                        }}
                      >
                        <span className="flex items-center gap-1 w-full">
                          <span className="shrink-0">
                            [{citation.display_index}]{' '}
                          </span>
                          <span className="flex-1" title={citation.title}>
                            {citation.title.slice(-70)}
                          </span>
                        </span>
                      </Button>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  );
}
